import api from './api';

/** A child Task sitting in one of the board's state columns. */
export interface BoardTask {
  id: number;
  title: string;
  state: string;
  type: string;
  assignedTo: string | null;
  /** DevOps tags, already split from the semicolon-delimited field. */
  tags: string[];
  url: string;
}

/** A parent User Story / Bug — one draggable row. */
export interface BoardRow {
  id: number;
  title: string;
  state: string;
  type: string;
  assignedTo: string | null;
  tags: string[];
  /** When DevOps first received the item. */
  createdDate: string | null;
  /**
   * Who actually asked for the work. Everything filed from GWI360 is created by the
   * shared PAT's owner, so this is mined from the reporter tag or description instead.
   */
  requester: { name: string; email: string } | null;
  rank: number | null;
  url: string;
  isClosed: boolean;
  /** Child tasks keyed by state column. Every column key is always present. */
  children: Record<string, BoardTask[]>;
}

export interface SprintBoard {
  columns: string[];
  rows: BoardRow[];
  iterationPath: string;
  /** False when the user hasn't connected their own PAT — no drag handles. */
  canReorder: boolean;
}

export interface Iteration {
  id: string;
  name: string;
  path: string;
}

export interface TokenStatus {
  connected: boolean;
  /** False when the environment has no encryption key configured at all. */
  available: boolean;
  identity?: string;
  lastValidated?: string | null;
  reason?: string;
}

/** Full work item, as shown in the detail modal. */
export interface WorkItemDetail {
  id: number;
  title: string;
  /** HTML from DevOps' editor — never render this as markup. */
  description: string;
  /** A Bug's body — DevOps fills this instead of description on bugs. Same HTML rules. */
  reproSteps: string;
  state: string;
  reason: string;
  areaPath: string;
  iterationPath: string;
  type: string;
  assignedTo: string | null;
  tags: string[];
  changedDate: string | null;
  url: string;
}

export interface WorkItemComment {
  id: number;
  text: string;
  createdBy: string | null;
  createdDate: string | null;
}

export interface Attachment {
  id: string;
  name: string;
  url: string;
  size: number | null;
  comment: string;
}

export interface TeamMember {
  name: string;
  /** The identity DevOps resolves on write — display names aren't unique. */
  email: string;
}

export interface FieldOptions {
  states: string[];
  areaPaths: string[];
  iterations: Iteration[];
  members: TeamMember[];
}

/** Only the fields the user actually touched; anything omitted is left alone. */
export type WorkItemChanges = Partial<{
  assignedTo: string;
  title: string;
  description: string;
  reproSteps: string;
  state: string;
  reason: string;
  areaPath: string;
  iterationPath: string;
}>;

export const devopsBoardService = {
  async listIterations(): Promise<{ data: Iteration[]; team: string }> {
    const res = await api.get('/devops-board/iterations');
    return res.data;
  },

  async getSprint(iterationPath: string): Promise<SprintBoard> {
    const res = await api.get('/devops-board/sprint', { params: { iterationPath } });
    return res.data;
  },

  /**
   * Move a row to a new priority position.
   *
   * `visibleIds` is the on-screen order with filters applied; the backend maps the
   * drop onto the sprint's true ranked order so hidden rows can't scramble it.
   */
  async reorder(params: {
    iterationId: string;
    iterationPath: string;
    movedId: number;
    targetIndex: number;
    visibleIds: number[];
  }): Promise<void> {
    await api.post('/devops-board/reorder', params);
  },

  /**
   * Move a task to another state — dropping its card in a different column.
   *
   * Returns the item as DevOps now holds it: taking a task off New also assigns it to
   * the caller, so the assignee in the response can differ from what was sent.
   */
  async setState(
    workItemId: number,
    state: string,
  ): Promise<{ id: number; state: string; assignedTo: string | null }> {
    const res = await api.post(`/devops-board/work-item/${workItemId}/state`, { state });
    return res.data.data;
  },

  /** Items with no sprint yet, in stack-rank order, plus the sprints to assign into. */
  async getBacklog(includeDone = false): Promise<{ data: BoardRow[]; iterations: Iteration[] }> {
    const res = await api.get('/devops-board/backlog', { params: { includeDone } });
    return res.data;
  },

  /** Raise a new story, bug, or task. Pass parentId to hang a task off a story. */
  async createWorkItem(params: {
    workItemType: 'User Story' | 'Bug' | 'Task';
    title: string;
    iterationPath?: string;
    parentId?: number;
    assignedTo?: string;
    /** HTML. On a Bug the backend writes it to Repro Steps, not System.Description. */
    description?: string;
  }): Promise<BoardRow> {
    const res = await api.post('/devops-board/work-item', params);
    return res.data.data;
  },

  async getWorkItem(id: number): Promise<WorkItemDetail> {
    const res = await api.get(`/devops-board/work-item/${id}`);
    return res.data.data;
  },

  async updateWorkItem(id: number, changes: WorkItemChanges): Promise<WorkItemDetail> {
    const res = await api.patch(`/devops-board/work-item/${id}`, changes);
    return res.data.data;
  },

  async getComments(id: number): Promise<WorkItemComment[]> {
    const res = await api.get(`/devops-board/work-item/${id}/comments`);
    return res.data.data;
  },

  async addComment(id: number, text: string): Promise<WorkItemComment> {
    const res = await api.post(`/devops-board/work-item/${id}/comments`, { text });
    return res.data.data;
  },

  async getAttachments(id: number): Promise<Attachment[]> {
    const res = await api.get(`/devops-board/work-item/${id}/attachments`);
    return res.data.data;
  },

  async addAttachment(id: number, file: File, comment = ''): Promise<Attachment> {
    const form = new FormData();
    form.append('file', file);
    form.append('comment', comment);
    const res = await api.post(`/devops-board/work-item/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return res.data.data;
  },

  /**
   * Upload an image pasted into a rich-text field.
   *
   * Deliberately NOT linked to the work item: like DevOps' own editor, a pasted image
   * lives only in the field's HTML and stays out of the Attachments list.
   */
  async uploadInlineImage(id: number, file: File): Promise<Attachment> {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`/devops-board/work-item/${id}/attachments`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      params: { link: false },
    });
    return res.data.data;
  },

  async removeAttachment(id: number, url: string): Promise<void> {
    await api.delete(`/devops-board/work-item/${id}/attachments`, { params: { url } });
  },

  /** Fetch through our API — the browser has no DevOps credentials. */
  async downloadAttachment(
    id: number,
    attachment: Pick<Attachment, 'id' | 'name'>,
  ): Promise<Blob> {
    const res = await api.get(
      `/devops-board/work-item/${id}/attachments/${attachment.id}`,
      { params: { fileName: attachment.name }, responseType: 'blob' },
    );
    return res.data;
  },

  async getOptions(workItemType: string): Promise<FieldOptions> {
    const res = await api.get('/devops-board/options', { params: { workItemType } });
    return res.data;
  },

  async getTokenStatus(): Promise<TokenStatus> {
    const res = await api.get('/devops-board/token');
    return res.data;
  },

  async saveToken(pat: string): Promise<{ connected: boolean; identity: string }> {
    const res = await api.put('/devops-board/token', { pat });
    return res.data;
  },

  async deleteToken(): Promise<void> {
    await api.delete('/devops-board/token');
  },
};

export default devopsBoardService;
